import { useEffect, useRef, useState, useCallback } from "react";
import { StyleSheet, Text, View, type StyleProp, type TextStyle, type ViewStyle } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withSequence,
  Easing,
  runOnJS,
} from "react-native-reanimated";

export type FloatingFeedbackProps = {
  /** The icon content (emoji, image, component). */
  children: React.ReactNode;
  /** The text that floats away on each trigger (e.g. "+1"). Captured at spawn time. */
  floatingText?: string;
  /** Custom renderer for the floating content. Receives the captured text. Overrides floatingText/floatingTextStyle. */
  renderFloatingContent?: (text: string) => React.ReactNode;
  /** Change this value to spawn a floating particle. */
  trigger: unknown;
  /** Style applied to the container. */
  style?: StyleProp<ViewStyle>;
  /** Style applied to the default floating text. Ignored when renderFloatingContent is set. */
  floatingTextStyle?: StyleProp<TextStyle>;
  /** How far the particle floats upward in px. Default 50. */
  floatDistance?: number;
  /** Float-away duration in ms. Default 800. */
  floatDuration?: number;
  /** Container size. Default 56. */
  size?: number;
};

export function FloatingFeedback({
  children,
  floatingText = "+1",
  renderFloatingContent,
  trigger,
  style,
  floatingTextStyle,
  floatDistance = 50,
  floatDuration = 800,
  size = 56,
}: FloatingFeedbackProps) {
  const isFirstRender = useRef(true);
  const [particles, setParticles] = useState<{ id: number; text: string }[]>([]);

  const removeParticle = useCallback((id: number) => {
    setParticles((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const floatingTextRef = useRef(floatingText);
  useEffect(() => {
    floatingTextRef.current = floatingText;
  }, [floatingText]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    setParticles((prev) => [...prev, { id: Date.now(), text: floatingTextRef.current }]);
  }, [trigger]);

  return (
    <View style={[{ width: size, height: size }, style]}>
      <View style={styles.icon}>
        {children}
      </View>

      {particles.map((p) => (
        <FloatingParticle
          key={p.id}
          id={p.id}
          text={p.text}
          textStyle={floatingTextStyle}
          renderContent={renderFloatingContent}
          distance={floatDistance}
          duration={floatDuration}
          onDone={removeParticle}
        />
      ))}
    </View>
  );
}

function FloatingParticle({
  id,
  text,
  textStyle,
  renderContent,
  distance,
  duration,
  onDone,
}: {
  id: number;
  text: string;
  textStyle?: StyleProp<TextStyle>;
  renderContent?: (text: string) => React.ReactNode;
  distance: number;
  duration: number;
  onDone: (id: number) => void;
}) {
  const startOffset = distance * 0.35;
  const translateY = useSharedValue(-startOffset);
  const opacity = useSharedValue(0);

  useEffect(() => {
    opacity.value = withSequence(
      withTiming(1, { duration: 120 }),
      withTiming(0, {
        duration: duration - 120,
        easing: Easing.in(Easing.quad),
      }),
    );
    translateY.value = withTiming(-distance, {
      duration,
      easing: Easing.out(Easing.cubic),
    }, () => {
      runOnJS(onDone)(id);
    });
  }, []);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
    opacity: opacity.value,
  }));

  return (
    <Animated.View style={[styles.particle, animatedStyle]} pointerEvents="none">
      {renderContent
        ? renderContent(text)
        : <Text numberOfLines={1} style={[styles.particleText, textStyle]}>{text}</Text>
      }
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  icon: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  particle: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    overflow: "visible",
  },
  particleText: {
    fontSize: 14,
    fontWeight: "800",
    color: "#4ade80",
  },
});
